import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Wallet, CreditCard, CheckCircle, 
  AlertCircle, RefreshCw, Lock, AlertTriangle, User
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { 
  createChatEscrow, processEscrowWalletPayment, initializeEscrowCardPayment,
  getWalletBalance, getActiveConversationEscrow, getConversations, calculateEscrowFees 
} from '../../services/api';
import Loading from '../../components/common/Loading';
import EscrowFeeBreakdown from '../../components/chat/EscrowFeeBreakdown';

const EscrowPaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  
  // Get data from navigation state
  const stateData = location.state || {};
  // console.log('Navigation state:', stateData); // Debug
  
  const conversationId = parseInt(id, 10);
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [escrowCreated, setEscrowCreated] = useState(false);
  const [escrowId, setEscrowId] = useState(null);
  
  // Use state data initially, then try to fetch from API if needed
  const [conversation, setConversation] = useState(null);
  const [sellerId, setSellerId] = useState(stateData.sellerId || null);
  const [sellerName, setSellerName] = useState(stateData.sellerName || 'Seller');
  const [listingTitle, setListingTitle] = useState(stateData.listingTitle || 'Item');
  
  // Payment state
  const [amount, setAmount] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('wallet');
  const [validationError, setValidationError] = useState('');
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  
  // Initialize
useEffect(() => {
  if (conversationId && user) {
    // console.log('Initializing with conversationId:', conversationId, 'user:', user.user_id);
    // console.log('Navigation state data:', stateData);
    
    // Check if we have an escrowId from navigation state (from pending escrow)
    if (stateData?.escrowId) {
      // console.log('Using existing escrow ID from state:', stateData.escrowId);
      setEscrowId(stateData.escrowId);
      setEscrowCreated(true);
      
      // If we have existing amount, pre-fill it
      if (stateData?.existingAmount) {
        setAmount(stateData.existingAmount.toString());
        calculateFeesFromBackend(parseFloat(stateData.existingAmount));
      }
      
      // Don't check for existing escrows since we're continuing an existing one
      // But still load conversation data and wallet balance
      fetchConversationData();
      fetchWalletBalance();
    } else {
      // No existing escrowId from state - check if there's one in the system
      fetchConversationData();
      checkExistingEscrow();
      fetchWalletBalance();
    }
  }
}, [conversationId, user]);
  
  const fetchConversationData = async () => {
    try {
      // console.log('Fetching conversations for user:', user.user_id);
      const response = await getConversations(user.user_id);
      
      // console.log('Conversations API response:', response);
      
      // Your API returns data directly as array, not wrapped in {success: true, data: [...]}
      const conversations = response.data || response; // Handle both cases
      
      if (Array.isArray(conversations)) {
        // Find the specific conversation by conversation_id
        const foundConversation = conversations.find(
          conv => conv.conversation_id === conversationId
        );
        
        // console.log('Found conversation:', foundConversation);
        
        if (foundConversation) {
          setConversation(foundConversation);
          
          // Only update if we don't already have data from state
          if (!stateData.sellerId) {
            setSellerId(foundConversation.seller_id);
          }
          
          if (!stateData.sellerName) {
            setSellerName(foundConversation.seller_name || 
                         `${foundConversation.seller_first_name || ''} ${foundConversation.seller_last_name || ''}`.trim() || 'Seller');
          }
          
          if (!stateData.listingTitle) {
            setListingTitle(foundConversation.listing_title || 'Item');
          }
        } else {
          // console.warn('Conversation not found in API response');
          // Don't show error if we have data from state
          if (!stateData.sellerId) {
            // console.error('Conversation not found and no state data');
          }
        }
      } else {
        // console.error('Conversations is not an array:', conversations);
        if (!stateData.sellerId) {
          // console.error('No valid conversations data and no state data');
        }
      }
    } catch (error) {
      // console.error('Error fetching conversation data:', error);
      // Only log error, don't redirect if we have state data
      if (!stateData.sellerId) {
        // console.error('No state data and API fetch failed');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const checkExistingEscrow = async () => {
    try {
      const response = await getActiveConversationEscrow(conversationId);
      
      if (response.success && response.data) {
        const existingEscrow = response.data;
        // console.log('Found existing escrow:', existingEscrow);
        
        // Check the status of existing escrow
        if (existingEscrow.status !== 'pending') {
          // If escrow is not pending (already held, released, etc.), redirect to chat
          navigate(`/chats/${conversationId}?activeEscrow=true`);
          return;
        }
        
        // If escrow is pending, use it
        setEscrowId(existingEscrow.escrow_id);
        setEscrowCreated(true);
        
        // Pre-fill the amount if not already set
        if (!amount && existingEscrow.amount) {
          setAmount(existingEscrow.amount.toString());
          calculateFeesFromBackend(parseFloat(existingEscrow.amount));
        }
        
        // console.log('Using pending escrow:', existingEscrow.escrow_id);
      }
    } catch (error) {
      // console.error('Error checking escrow:', error);
    }
  };
  
  const fetchWalletBalance = async () => {
    try {
      const response = await getWalletBalance(user.user_id);
      // console.log('Wallet response:', response);
      
      // Handle different response structures
      let balance = 0;
      
      if (response.success !== false) {
        // Your wallet API returns {success: true, balance: "233000.08", wallet: {...}}
        if (response.balance) {
          balance = parseFloat(response.balance);
        } else if (response.data?.balance) {
          balance = parseFloat(response.data.balance);
        } else if (response.wallet?.balance) {
          balance = parseFloat(response.wallet.balance);
        }
      }
      
      setWalletBalance(balance);
      
      // Set suggested amount (10% of balance or 5000)
      const suggestedAmount = Math.min(5000, Math.floor(balance * 0.1));
      if (suggestedAmount >= 1000) {
        setAmount(suggestedAmount.toString());
      }
    } catch (error) {
      // console.error('Error fetching wallet:', error);
      // Don't crash if wallet fetch fails, just show 0 balance
      setWalletBalance(0);
    }
  };
  
  const validateAmount = () => {
    const numericAmount = parseFloat(amount);
    
    if (!amount || isNaN(numericAmount)) {
      setValidationError('Please enter a valid amount');
      return false;
    }
    
    if (numericAmount < 1000) {
      setValidationError('Minimum escrow amount is ₦1,000');
      return false;
    }
    
    if (numericAmount > 10000000) { // ₦10M limit
      setValidationError('Maximum escrow amount is ₦10,000,000');
      return false;
    }
    
    if (!sellerId) {
      setValidationError('Seller information not available');
      return false;
    }
    
    setValidationError('');
    return true;
  };
  
  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const createEscrow = async () => {
    // If we already have an escrowId (from pending state), don't create a new one
    if (escrowId && escrowCreated) {
      // console.log('Using existing escrow:', escrowId);
      return escrowId; // Return the existing escrow ID
    }
    
    if (!validateAmount()) return null;
    
    try {
      const numericAmount = parseFloat(amount);
      
      const response = await createChatEscrow({
        conversation_id: conversationId,
        buyer_id: user.user_id,
        seller_id: sellerId,
        amount: numericAmount,
        listing_title: listingTitle,
        message: 'Escrow deposit from chat'
      });
      
      // console.log('createChatEscrow response:', response);
      
      if (response.success) {
        const newEscrowId = response.escrow_id;
        
        if (!newEscrowId) {
          // console.error('No escrow_id in response:', response);
          alert('Failed to get escrow ID from server');
          return null;
        }
        
        const escrowIdNumber = parseInt(newEscrowId, 10);
        
        setEscrowId(escrowIdNumber);
        setEscrowCreated(true);
        
        // console.log('New escrow created with ID:', escrowIdNumber);
        
        calculateFeesFromBackend(numericAmount);
        
        return escrowIdNumber;
      } else {
        throw new Error(response.error || 'Failed to create escrow');
      }
    } catch (error) {
      // console.error('Error creating escrow:', error);
      alert(`❌ Failed to create escrow: ${error.message}`);
      return null;
    }
  };

   // Function to calculate fees from backend
   const calculateFeesFromBackend = async (amountValue) => {
    const numericAmount = parseFloat(amountValue);
    
    if (!amountValue || isNaN(numericAmount) || numericAmount < 1000) {
      setFeeBreakdown(null);
      return;
    }
    
    setFeeLoading(true);
    
    try {
      const response = await calculateEscrowFees(numericAmount);
      
      if (response.success) {
        setFeeBreakdown(response.fee_breakdown);
        // You can also store the display info
      } else {
        // Fallback to client-side calculation
        calculateFeesFallback(numericAmount);
      }
    } catch (error) {
      // console.error('Error fetching fees from backend:', error);
      // Fallback to client-side calculation
      calculateFeesFallback(numericAmount);
    } finally {
      setFeeLoading(false);
    }
  };
  
  // Fallback calculation (use if backend fails)
  const calculateFeesFallback = (amount) => {
    // Simplified calculation based on your FeeCalculator logic
    // Adjust these percentages based on your actual fee structure
    const platformFeePercentage = 0.01; // 1% platform fee
    const paystackFeePercentage = 0.015; // 1.5% Paystack fee
    const buyerPercentage = 50; // 50% buyer share
    const sellerPercentage = 50; // 50% seller share
    
    const platformFee = amount * platformFeePercentage;
    const paystackFee = amount * paystackFeePercentage;
    const totalFees = platformFee + paystackFee;
    const buyerFeeShare = (totalFees * buyerPercentage) / 100;
    const sellerFeeShare = (totalFees * sellerPercentage) / 100;
    
    setFeeBreakdown({
      product_amount: amount,
      platform_fee: platformFee,
      paystack_fee: paystackFee,
      total_fees: totalFees,
      buyer_fee_share: buyerFeeShare,
      seller_fee_share: sellerFeeShare,
      buyer_pays_total: amount + buyerFeeShare,
      seller_receives_net: amount - sellerFeeShare,
      transaction_type: 'purchase'
    });
  };

  const handleAmountChange = (value) => {
    setAmount(value);
    
    if (value && parseFloat(value) > 0) {
      // Debounce the fee calculation
      const timeoutId = setTimeout(() => {
        calculateFeesFromBackend(value);
      }, 300);
      
      return () => clearTimeout(timeoutId);
    } else {
      setFeeBreakdown(null);
    }
  };
  
  
  const handlePayment = async () => {
    if (!validateAmount()) return;
    if (processing) return;
    
    setProcessing(true);
    
    try {
      let currentEscrowId = escrowId;
      
      // Create escrow first if not created
      if (!escrowCreated) {
        // console.log('Creating escrow...');
        const newEscrowId = await createEscrow();
        
        if (!newEscrowId) {
          setProcessing(false);
          return;
        }
        
        // Use the returned escrow ID immediately
        currentEscrowId = newEscrowId;
        // console.log('Using new escrow ID:', currentEscrowId);
      }
      
      // Double check escrowId
      if (!currentEscrowId || currentEscrowId === 'null' || currentEscrowId === 'undefined') {
        // console.error('Invalid escrowId:', currentEscrowId);
        alert('Error: Escrow ID is missing. Please try again.');
        setProcessing(false);
        return;
      }
      
      const numericAmount = parseFloat(amount);
      
      // Check wallet balance if paying with wallet
      if (paymentMethod === 'wallet') {
        const requiredAmount = feeBreakdown ? feeBreakdown.buyer_pays_total : numericAmount;
        
        if (requiredAmount > walletBalance) {
          if (!window.confirm(
            `Insufficient wallet balance.\n\nYou need ${formatPrice(requiredAmount - walletBalance)} more.\n\nSwitch to card payment?`
          )) {
            setProcessing(false);
            return;
          }
          setPaymentMethod('card');
          setProcessing(false);
          return;
        }
        
        // DEBUG: Log before payment
        // console.log('Processing wallet payment for escrow ID:', currentEscrowId);
        // console.log('Payment amount:', requiredAmount);
        // console.log('User ID:', user.user_id);
        
        // Process wallet payment
        const result = await processEscrowWalletPayment(currentEscrowId, {
          user_id: user.user_id
        });
        
        // console.log('Wallet payment result:', result);
        
        if (result.success) {
          alert(`✅ Success! ${formatPrice(numericAmount)} secured in escrow.`);
          navigate('/chats');
        } else {
          throw new Error(result.error || 'Wallet payment failed');
        }
        
      } else {
        // Card payment
        const result = await initializeEscrowCardPayment(currentEscrowId, {
          user_id: user.user_id,
          email: user.email
        });
        
        if (result.success && result.authorization_url) {
          // Open Paystack in new tab
          window.open(result.authorization_url, '_blank', 'noopener,noreferrer');
          
          alert('🔄 Redirecting to payment gateway...\n\nReturn to chat after payment.');
          navigate('/chats');
        } else {
          throw new Error(result.error || 'Card payment failed');
        }
      }
      
    } catch (error) {
      // console.error('Payment error:', error);
      alert(`❌ Payment failed: ${error.message || 'Please try again'}`);
      setProcessing(false);
    }
  };
  
  const handleBack = () => {
    navigate('/chats');
  };
  
  const handleAmountQuickSelect = (selectedAmount) => {
    setAmount(selectedAmount.toString());
    calculateFeesFromBackend(selectedAmount.toString());
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Loading escrow information..." />
      </div>
    );
  }
  
  // Check if we have required data
  if (!sellerId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Missing Information</h2>
          <p className="text-gray-600 mb-4">
            {!conversationId ? 'Conversation ID is missing.' : 'Seller information is missing.'}
            <br />
            <span className="text-sm text-gray-500">
              Please go back to the chat and try the escrow payment again.
            </span>
          </p>
          <div className="space-y-3">
            <button
              onClick={handleBack}
              className="w-full bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
            >
              Back to Chat
            </button>
            <button
              onClick={() => navigate('/chats')}
              className="w-full border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Back to All Chats
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // console.log('Rendering with sellerId:', sellerId, 'sellerName:', sellerName, 'listingTitle:', listingTitle);
  
  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-white shadow sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {escrowCreated && escrowId ? 'Complete Escrow Payment' : 'Pay into Escrow'}
              </h1>
              <p className="text-sm text-gray-600">Secure payment for {listingTitle}</p>
              <div className="flex items-center gap-2 mt-1">
                <User className="w-3 h-3 text-gray-500" />
                <p className="text-xs text-gray-500">With {sellerName}</p>
                {escrowCreated && escrowId && (
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                    Escrow #{escrowId}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Conversation Info Card */}
        <div className="bg-white rounded-xl shadow-sm p-5 border">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white font-bold flex-shrink-0">
              {sellerName.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Transaction Details</h3>
              <div className="mt-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Seller</span>
                  <span className="text-sm font-medium text-gray-900">{sellerName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Item</span>
                  <span className="text-sm font-medium text-gray-900">{listingTitle}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Conversation ID</span>
                  <span className="text-sm font-medium text-gray-900">#{conversationId}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Info Card */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <div>
              <h2 className="font-semibold text-gray-900 mb-1">How Escrow Works</h2>
              <ul className="text-sm text-gray-700 space-y-1">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Your payment is held securely until you receive the item</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Funds are only released to seller after you confirm receipt</span>
                </li>
              
              </ul>
            </div>
          </div>
        </div>
        
        {/* Amount Input Card */}
        <div className="bg-white rounded-xl shadow-sm p-5 border">
          <h2 className="font-semibold text-gray-900 mb-4">Escrow Amount</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How much do you want to secure in escrow?
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold">₦</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                className="w-full pl-10 pr-4 py-4 text-xl border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="0.00"
                min="1000"
                step="100"
              />
            </div>

          
            {feeLoading && (
              <div className="mt-2 text-center">
                <div className="inline-flex items-center gap-2 text-sm text-gray-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                  Calculating fees...
                </div>
              </div>
            )}
            
            {validationError && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {validationError}
              </p>
            )}
            
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => handleAmountQuickSelect(1000)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ₦1,000
              </button>
              <button
                onClick={() => handleAmountQuickSelect(5000)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ₦5,000
              </button>
              <button
                onClick={() => handleAmountQuickSelect(10000)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ₦10,000
              </button>
              <button
                onClick={() => handleAmountQuickSelect(50000)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ₦50,000
              </button>
            </div>
          </div>
        </div>
        
        {/* Fee Breakdown */}
        {feeBreakdown && (
          <EscrowFeeBreakdown feeData={feeBreakdown} />
        )}
        
        {/* Wallet Balance */}
        <div className="bg-white rounded-xl shadow-sm p-5 border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Wallet className="w-6 h-6 text-orange-500" />
              <div>
                <h3 className="font-semibold text-gray-900">Wallet Balance</h3>
                <p className="text-sm text-gray-600">Available for escrow payments</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">
                {formatPrice(walletBalance)}
              </p>
              <button
                onClick={fetchWalletBalance}
                className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1 mt-1"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </button>
            </div>
          </div>
          
          {paymentMethod === 'wallet' && feeBreakdown && feeBreakdown.buyer_pays_total > walletBalance && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-yellow-800">Insufficient Balance</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    You need {formatPrice(feeBreakdown.buyer_pays_total - walletBalance)} more to pay {formatPrice(feeBreakdown.buyer_pays_total)}.
                    Switch to card payment or add funds to your wallet.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Payment Methods */}
        <div className="bg-white rounded-xl shadow-sm p-5 border">
          <h2 className="font-semibold text-gray-900 mb-4">Payment Method</h2>
          
          <div className="space-y-3">
            {/* Wallet Option */}
            <button
              onClick={() => setPaymentMethod('wallet')}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all ${paymentMethod === 'wallet' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${paymentMethod === 'wallet' ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <Wallet className={`w-5 h-5 ${paymentMethod === 'wallet' ? 'text-green-600' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Pay with Wallet</h3>
                    <p className="text-sm text-gray-600">Use your wallet balance</p>
                  </div>
                </div>
                {paymentMethod === 'wallet' && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
              </div>
            </button>
            
            {/* Card Option */}
            <button
              onClick={() => setPaymentMethod('card')}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all ${paymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${paymentMethod === 'card' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                    <CreditCard className={`w-5 h-5 ${paymentMethod === 'card' ? 'text-blue-600' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Pay with Card</h3>
                    <p className="text-sm text-gray-600">Debit/Credit card via Paystack</p>
                  </div>
                </div>
                {paymentMethod === 'card' && (
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                )}
              </div>
            </button>
          </div>
        </div>
        
        {/* Security Notice */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-green-800">Escrow Protection</h3>
              <p className="text-sm text-green-700 mt-1">
                Your payment is held securely in escrow until you confirm receipt of the item. 
                Funds are only released to the seller after you verify delivery.
              </p>
            </div>
          </div>
        </div><br /><br />
      </main>
      
      {/* Fixed Payment Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total to pay</p>
              <p className="text-2xl font-bold text-gray-900">
                {feeBreakdown ? formatPrice(feeBreakdown.buyer_pays_total) : formatPrice(amount || 0)}
              </p>
              {feeBreakdown && (
                <p className="text-xs text-gray-500 mt-1">
                  Includes {formatPrice(feeBreakdown.buyer_fee_share)} fees
                </p>
              )}
            </div>
            
            <button
              onClick={handlePayment}
              disabled={processing || !amount || parseFloat(amount) < 1000 || !!validationError}
              className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  {escrowCreated && escrowId ? 'Complete Payment' : 'Pay into Escrow'}
                </>
              )}
            </button>
          </div>
          
          <p className="text-xs text-gray-500 text-center mt-3">
            By proceeding, you agree to our Escrow Terms and Payment Protection Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default EscrowPaymentPage;